import { CliError, EXIT_INPUT } from "../../../common.js";
import {
  ACTION_TYPE_SET,
  IMPORTANCE_TIER_SET,
  NON_ACTOR_SPEAKER_KIND_SET,
  ROLE_TYPE_SET,
  type ExtensionMap,
} from "../shared/types.js";
import { isV4Id } from "./ids.js";
import type {
  ActionV4,
  ActorRefV4,
  ActorSpeakerV4,
  ActorV4,
  EpisodeV4,
  LocationRefV4,
  LocationV4,
  NonActorSpeakerV4,
  PropRefV4,
  PropV4,
  SceneV4,
  ScriptV4,
  StateIdV4,
  StateV4,
} from "./types.js";
import { validateScriptV4 } from "./validation.js";

type RecordValue = Record<string, unknown>;

function isRecord(value: unknown): value is RecordValue {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function optionalString(record: RecordValue, key: string): boolean {
  return record[key] === undefined || typeof record[key] === "string";
}

function isJsonValue(value: unknown): boolean {
  if (
    value === null
    || typeof value === "string"
    || typeof value === "number"
    || typeof value === "boolean"
  ) return true;
  if (Array.isArray(value)) return value.every(isJsonValue);
  return isRecord(value) && Object.values(value).every(isJsonValue);
}

function hasValidExtension(record: RecordValue): record is RecordValue & { extend?: ExtensionMap } {
  return record.extend === undefined
    || (isRecord(record.extend) && Object.values(record.extend).every(isJsonValue));
}

function isStateId(value: unknown): value is StateIdV4 {
  return value === "default" || isV4Id("state", value);
}

function isState(value: unknown): value is StateV4 {
  return isRecord(value)
    && isStateId(value.state_id)
    && typeof value.state_name === "string"
    && typeof value.description === "string"
    && hasValidExtension(value);
}

function hasAssetShape(value: RecordValue): boolean {
  return typeof value.importance === "string"
    && IMPORTANCE_TIER_SET.has(value.importance)
    && typeof value.description === "string"
    && (value.aliases === undefined
      || (Array.isArray(value.aliases) && value.aliases.every((alias) => typeof alias === "string")))
    && Array.isArray(value.states)
    && value.states.every(isState)
    && hasValidExtension(value);
}

function isActor(value: unknown): value is ActorV4 {
  return isRecord(value)
    && isV4Id("actor", value.actor_id)
    && typeof value.actor_name === "string"
    && (value.role_type === undefined
      || (typeof value.role_type === "string" && ROLE_TYPE_SET.has(value.role_type)))
    && hasAssetShape(value);
}

function isLocation(value: unknown): value is LocationV4 {
  return isRecord(value)
    && isV4Id("location", value.location_id)
    && typeof value.location_name === "string"
    && hasAssetShape(value);
}

function isProp(value: unknown): value is PropV4 {
  return isRecord(value)
    && isV4Id("prop", value.prop_id)
    && typeof value.prop_name === "string"
    && hasAssetShape(value);
}

function isStateIds(value: unknown): value is StateIdV4[] {
  return Array.isArray(value) && value.every(isStateId);
}

function isActorRef(value: unknown): value is ActorRefV4 {
  return isRecord(value)
    && isV4Id("actor", value.actor_id)
    && isStateIds(value.state_ids);
}

function isLocationRef(value: unknown): value is LocationRefV4 {
  return isRecord(value)
    && isV4Id("location", value.location_id)
    && isStateIds(value.state_ids);
}

function isPropRef(value: unknown): value is PropRefV4 {
  return isRecord(value)
    && isV4Id("prop", value.prop_id)
    && isStateIds(value.state_ids);
}

function isActorSpeaker(value: RecordValue): value is RecordValue & ActorSpeakerV4 {
  return isV4Id("actor", value.actor_id)
    && (value.state_id === undefined || isStateId(value.state_id));
}

function isNonActorSpeaker(value: RecordValue): value is RecordValue & NonActorSpeakerV4 {
  return typeof value.kind === "string"
    && NON_ACTOR_SPEAKER_KIND_SET.has(value.kind)
    && optionalString(value, "label");
}

function isAction(value: unknown): value is ActionV4 {
  if (
    !isRecord(value)
    || !isV4Id("action", value.action_id)
    || typeof value.type !== "string"
    || !ACTION_TYPE_SET.has(value.type)
    || typeof value.content !== "string"
    || !optionalString(value, "parenthetical")
    || !hasValidExtension(value)
  ) return false;
  if (value.type === "dialogue" || value.type === "inner_thought") {
    if (!isRecord(value.speaker)
      || (!isActorSpeaker(value.speaker) && !isNonActorSpeaker(value.speaker))) return false;
  }
  if (value.transition_prompt !== undefined) {
    if (
      !isRecord(value.transition_prompt)
      || (!isV4Id("actor", value.transition_prompt.target)
        && !isV4Id("location", value.transition_prompt.target)
        && !isV4Id("prop", value.transition_prompt.target))
      || typeof value.transition_prompt.process !== "string"
      || typeof value.transition_prompt.contrast !== "string"
    ) return false;
  }
  return true;
}

function isScene(value: unknown): value is SceneV4 {
  if (
    !isRecord(value)
    || !isV4Id("scene", value.scene_id)
    || !Array.isArray(value.actors)
    || !value.actors.every(isActorRef)
    || !Array.isArray(value.props)
    || !value.props.every(isPropRef)
    || !Array.isArray(value.actions)
    || !value.actions.every(isAction)
    || !hasValidExtension(value)
  ) return false;
  if (value.location !== null && !isLocationRef(value.location)) return false;
  if (value.environment !== undefined
    && (!isRecord(value.environment)
      || !optionalString(value.environment, "space")
      || !optionalString(value.environment, "time"))) return false;
  return true;
}

function isEpisode(value: unknown): value is EpisodeV4 {
  return isRecord(value)
    && isV4Id("episode", value.episode_id)
    && optionalString(value, "title")
    && optionalString(value, "synopsis")
    && Array.isArray(value.scenes)
    && value.scenes.every(isScene)
    && hasValidExtension(value);
}

export function isStructurallyReadableScriptV4(value: unknown): value is ScriptV4 {
  return isRecord(value)
    && value.version === 4
    && typeof value.title === "string"
    && typeof value.worldview === "string"
    && typeof value.style === "string"
    && optionalString(value, "worldview_raw")
    && optionalString(value, "synopsis")
    && optionalString(value, "logline")
    && optionalString(value, "theme")
    && (value.main_characters === undefined
      || (Array.isArray(value.main_characters)
        && value.main_characters.every((item) =>
          isRecord(item) && typeof item.name === "string" && typeof item.role === "string")))
    && Array.isArray(value.actors)
    && value.actors.every(isActor)
    && Array.isArray(value.locations)
    && value.locations.every(isLocation)
    && Array.isArray(value.props)
    && value.props.every(isProp)
    && Array.isArray(value.episodes)
    && value.episodes.every(isEpisode)
    && hasValidExtension(value);
}

export function isScriptV4(value: unknown): value is ScriptV4 {
  return validateScriptV4(value).ok;
}

export function requireScriptV4(value: unknown): ScriptV4 {
  const validation = validateScriptV4(value);
  if (validation.ok) return value as ScriptV4;
  throw new CliError("The local file is not a valid Script v4 document.", {
          exitCode: EXIT_INPUT,
          errorCode: "SCRIPT_V4_INVALID",
          hint: "Run `scriptctl validate --script-path <path>` and repair the reported v4 fields.",
          data: { expected: ["A valid Script v4 document"], actual: validation.errors.slice(0, 20).map((issue) => `${issue.path}: ${issue.message}`) },
        });
}

export function requireStructurallyReadableScriptV4(value: unknown): ScriptV4 {
  if (isStructurallyReadableScriptV4(value)) return value;
  throw new CliError("The local file is not a structurally readable Script v4 document.", {
          exitCode: EXIT_INPUT,
          errorCode: "SCRIPT_V4_SHAPE_INVALID",
          hint: "Repair the local Script v4 structure, then rerun the command.",
          data: {
            expected: ["Script v4 with typed assets, episodes, scenes, references, and actions"],
            actual: [Array.isArray(value) ? "array" : typeof value],
          },
        });
}
